import React from 'react';
import { CheckCircle, Circle, Clock, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';

const phaseConfig = {
  gap_analysis: {
    label: 'Gap Analysis',
    description: 'Initial assessment and gap identification',
    number: 1,
  },
  application_implementation: {
    label: 'Application & Implementation',
    description: 'Application filing and implementation',
    number: 2,
  },
  desktop_nc_closure: {
    label: 'Desktop NC Closure',
    description: 'NC management and staff training',
    number: 3,
  },
  pre_assessment: {
    label: 'Pre-Assessment Readiness',
    description: 'Mock audit and compliance check',
    number: 4,
  },
  final_nc_closure: {
    label: 'Final NC Closure',
    description: 'Final assessment and accreditation',
    number: 5,
  },
};

const getStatusIcon = (status, readiness) => {
  if (status === 'completed') {
    return <CheckCircle className="w-6 h-6 text-green-500" />;
  }
  if (status === 'in_progress') {
    if (readiness === 'green') return <Clock className="w-6 h-6 text-green-500" />;
    if (readiness === 'amber') return <Clock className="w-6 h-6 text-amber-500" />;
    return <Clock className="w-6 h-6 text-red-500" />;
  }
  return <Circle className="w-6 h-6 text-slate-300" />;
};

const getStatusColor = (status, readiness) => {
  if (status === 'completed') return 'border-green-500 bg-green-50';
  if (status === 'in_progress') {
    if (readiness === 'green') return 'border-green-500 bg-green-50';
    if (readiness === 'amber') return 'border-amber-500 bg-amber-50';
    return 'border-red-500 bg-red-50';
  }
  return 'border-slate-200 bg-slate-50';
};

const getLineColor = (status, readiness) => {
  if (status === 'completed') return 'bg-green-500';
  if (status === 'in_progress') {
    if (readiness === 'green') return 'bg-green-500';
    if (readiness === 'amber') return 'bg-amber-500';
    return 'bg-red-500';
  }
  return 'bg-slate-200';
};

export const PhaseTracker = ({ phases = [], currentPhase, onPhaseClick, compact = false }) => {
  const orderedPhases = Object.keys(phaseConfig).map((key) => {
    const phase = phases.find((p) => p.phase_type === key);
    return {
      key,
      ...phaseConfig[key],
      ...phase,
      status: phase?.status || 'pending',
      readiness_status: phase?.readiness_status || 'red',
      completion_percentage: phase?.completion_percentage || 0,
    };
  });

  if (compact) {
    return (
      <div className="flex items-center gap-2" data-testid="phase-tracker-compact">
        {orderedPhases.map((phase, index) => (
          <React.Fragment key={phase.key}>
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-all cursor-pointer',
                getStatusColor(phase.status, phase.readiness_status),
                currentPhase === phase.key && 'ring-2 ring-primary ring-offset-2'
              )}
              onClick={() => onPhaseClick?.(phase)}
              title={phase.label}
              data-testid={`phase-dot-${phase.key}`}
            >
              {phase.status === 'completed' ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : (
                phase.number
              )}
            </div>
            {index < orderedPhases.length - 1 && (
              <div
                className={cn(
                  'h-0.5 w-8 transition-colors',
                  getLineColor(phase.status, phase.readiness_status)
                )}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="phase-tracker">
      {orderedPhases.map((phase, index) => (
        <div key={phase.key} className="relative">
          {/* Connector line */}
          {index < orderedPhases.length - 1 && (
            <div
              className={cn(
                'absolute left-5 top-12 w-0.5 h-8 transition-colors',
                getLineColor(phase.status, phase.readiness_status)
              )}
            />
          )}

          <div
            className={cn(
              'flex items-start gap-4 p-4 rounded-xl border-2 transition-all cursor-pointer hover:shadow-md',
              getStatusColor(phase.status, phase.readiness_status),
              currentPhase === phase.key && 'ring-2 ring-primary ring-offset-2'
            )}
            onClick={() => onPhaseClick?.(phase)}
            data-testid={`phase-card-${phase.key}`}
          >
            <div className="flex-shrink-0 mt-0.5">
              {getStatusIcon(phase.status, phase.readiness_status)}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h4 className="font-semibold text-foreground">
                  Phase {phase.number}: {phase.label}
                </h4>
                <span
                  className={cn(
                    'phase-badge',
                    phase.status === 'completed' && 'phase-green',
                    phase.status === 'in_progress' && phase.readiness_status === 'green' && 'phase-green',
                    phase.status === 'in_progress' && phase.readiness_status === 'amber' && 'phase-amber',
                    phase.status === 'in_progress' && phase.readiness_status === 'red' && 'phase-red',
                    phase.status === 'pending' && 'phase-pending'
                  )}
                >
                  {phase.status === 'completed' ? 'Completed' : phase.status === 'in_progress' ? 'In Progress' : 'Pending'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{phase.description}</p>

              {phase.status === 'in_progress' && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium">{phase.completion_percentage}%</span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all',
                        phase.readiness_status === 'green' && 'bg-green-500',
                        phase.readiness_status === 'amber' && 'bg-amber-500',
                        phase.readiness_status === 'red' && 'bg-red-500'
                      )}
                      style={{ width: `${phase.completion_percentage}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PhaseTracker;
